import React, { useCallback, useEffect, useMemo, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase";
import { AlertTriangle, Loader2, RefreshCw } from "lucide-react";

type PixPayoutBucket = "awaiting" | "paid" | "processing" | "failed";

interface PixPayoutRow {
  id: string;
  kind: "influencer" | "session";
  kindLabel: string;
  bucket: PixPayoutBucket;
  statusLabel: string;
  amount: number;
  recipientName: string;
  bankName: string;
  detailLines: string[];
  requestedAt?: number;
  asaasTransferId: string;
  failReason?: string;
}

interface ListResponse {
  awaitingCount: number;
  awaitingTotal: number;
  items: PixPayoutRow[];
}

type Filter = "awaiting" | "paid" | "all";

const money = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatDate = (epochMs?: number): string => {
  if (!epochMs) return "—";
  return new Date(epochMs).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const bucketStyle: Record<PixPayoutBucket, string> = {
  awaiting: "bg-amber-100 text-amber-900",
  processing: "bg-sky-100 text-sky-900",
  paid: "bg-emerald-100 text-emerald-900",
  failed: "bg-rose-100 text-rose-900",
};

const PixPayoutsPanel: React.FC = () => {
  const [items, setItems] = useState<PixPayoutRow[]>([]);
  const [awaitingCount, setAwaitingCount] = useState(0);
  const [awaitingTotal, setAwaitingTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("awaiting");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const callable = httpsCallable<Record<string, never>, ListResponse>(
        functions,
        "listPixPayoutsForAdmin"
      );
      const result = await callable({});
      setItems(result.data.items || []);
      setAwaitingCount(result.data.awaitingCount || 0);
      setAwaitingTotal(result.data.awaitingTotal || 0);
    } catch (err: unknown) {
      console.error(err);
      const message = err instanceof Error ? err.message : "Não foi possível carregar os PIX.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      if (filter === "awaiting" && item.bucket !== "awaiting") return false;
      if (filter === "paid" && item.bucket !== "paid") return false;
      if (!query) return true;
      const haystack = [
        item.recipientName,
        item.bankName,
        item.kindLabel,
        item.statusLabel,
        ...item.detailLines,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [items, filter, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-800 sm:text-xl">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            PIX a autorizar
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Repasses que o sistema já pediu para a Asaas. Enquanto estiver aguardando autorização,
            o dinheiro só sai depois do código na Asaas. Aqui dá para conferir o que cada um está pagando.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={isLoading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
          Atualizar
        </button>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
        {awaitingCount === 0
          ? "Nenhum PIX aguardando autorização agora."
          : `${awaitingCount} PIX aguardando autorização, somando ${money(awaitingTotal)}.`}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["awaiting", "Aguardando"],
              ["paid", "Pagos"],
              ["all", "Todos"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                filter === id
                  ? "bg-[#2B5A41] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <input
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar por nome, banco ou plano"
          className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#2B5A41]"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-gray-600">
          <Loader2 className="mr-2 h-6 w-6 animate-spin text-[#2B5A41]" />
          Consultando a Asaas...
        </div>
      ) : visible.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
          Nenhum PIX neste filtro.
        </div>
      ) : (
        <ul className="space-y-3">
          {visible.map((item) => (
            <li key={item.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${bucketStyle[item.bucket]}`}>
                      {item.statusLabel}
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {item.kindLabel}
                    </span>
                  </div>
                  <p className="mt-2 text-lg font-semibold text-gray-900">
                    {money(item.amount)} para {item.recipientName}
                  </p>
                  {item.bankName && (
                    <p className="text-sm text-gray-600">{item.bankName}</p>
                  )}
                </div>
                <p className="text-sm text-gray-500">Pedido em {formatDate(item.requestedAt)}</p>
              </div>
              <ul className="mt-3 space-y-1 text-sm leading-relaxed text-gray-700">
                {item.detailLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {item.failReason && item.bucket === "failed" && (
                <p className="mt-2 text-sm text-rose-700">{item.failReason}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default PixPayoutsPanel;
